package com.evoapps.evoshift.ui.roster

import com.evoapps.evoshift.widgets.ProvideDialogColors
import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.focusable
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.scrollBy
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.gestures.awaitLongPressOrCancellation
import androidx.compose.foundation.gestures.calculateZoom
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Person
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEvent
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isAltPressed
import androidx.compose.ui.input.key.isCtrlPressed
import androidx.compose.ui.input.key.isMetaPressed
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.PointerType
import androidx.compose.ui.input.pointer.isCtrlPressed
import androidx.compose.ui.input.pointer.isMetaPressed
import androidx.compose.ui.input.pointer.isPrimaryPressed
import androidx.compose.ui.input.pointer.isShiftPressed
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupPositionProvider
import androidx.compose.ui.window.PopupProperties
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DarkMetallicBackgroundBrush
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.viewmodels.GridCell
import com.evoapps.evoshift.viewmodels.isPending
import com.evoapps.evoshift.widgets.dialogKeys
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.close
import evoshift.composeapp.generated.resources.roster_grid_label
import evoshift.composeapp.generated.resources.roster_selected_count
import evoshift.composeapp.generated.resources.roster_selection_apply
import evoshift.composeapp.generated.resources.roster_selection_clear
import evoshift.composeapp.generated.resources.roster_selection_no_templates
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource
import kotlin.math.abs

/*
 * SH-169: the roster grid on a laptop, a desktop or a tablet with a keyboard and a pointer:
 *
 * - A cell's actions in a popover beside the cell (not a sheet at the bottom of a big screen).
 * - The keyboard: the arrows move a focus ring between people and days, Enter opens the cell,
 *   Delete unassigns its shifts (with Undo), Space selects it, Esc closes or clears. The grid is
 *   one stop for Tab (its cells are not), so Tab leaves it; screen readers still reach each cell
 *   by its own semantics and actions.
 * - The mouse: a shift chip dragged onto another person or day (a ghost follows the pointer, the
 *   cell under it is outlined); Shift, Ctrl or Cmd + click selects empty cells for a template.
 * - Ctrl + the wheel zooms the cells' width (as Ctrl +/- do); SH-161: so does a two-finger pinch.
 * - SH-171: the day headers: a click (or Up from the first person) puts the keyboard on a day;
 *   Shift + click or Shift + the arrows select a range of days, Ctrl / Cmd + click (or Space) adds
 *   or removes one; Ctrl/Cmd+C copies the selected days (or the focused one), Ctrl/Cmd+V pastes
 *   them from the focused day (or from each selected day). A paste always opens the "Copy days"
 *   dialog on its second step, so what does not fit and why shows before anything is written.
 */

/** Test tags of the grid. */
object RosterGridTags {
    const val GRID = "roster_grid"
    const val FOCUS_RING = "roster_grid_focus_ring"
    const val DROP_TARGET = "roster_grid_drop_target"
    const val DRAG_GHOST = "roster_grid_drag_ghost"
    const val POPOVER = "roster_cell_popover"
    const val SELECTION_BAR = "roster_selection_bar"
    const val SELECTION_APPLY = "roster_selection_apply"
    const val SELECTION_CLEAR = "roster_selection_clear"
    fun cell(userId: String, date: LocalDate) = "roster_cell_${userId}_$date"
    /** SH-171: a day's column header. */
    fun header(date: LocalDate) = "roster_day_header_$date"
    const val HEADER_RING = "roster_day_header_ring"
    fun template(id: String) = "roster_selection_template_$id"
}

/** A shift chip being dragged: from [from], under the pointer at [pointer] (in the grid), over [target]. */
data class GridDrag(val shift: Shift, val from: GridCell, val pointer: Offset, val target: GridCell?)

/**
 * Where the grid's rows and columns are, from its last layout (pixels in the grid's box): what
 * the pointer is over and how far to scroll to show a cell.
 */
internal class GridGeometry(
    val people: List<String>,
    val dates: List<LocalDate>,
    val sidebarPx: Float,
    val headerPx: Float,
    val cellPx: Float,
    /** A person's row without its divider. */
    val rowPx: Float,
    /** The cell's padding around its chips (top and bottom). */
    val cellPadPx: Float,
    val widthPx: Float,
    val list: LazyListState,
    val row: ScrollState,
    val shiftsOf: (GridCell) -> List<Shift>
) {
    private val personIndex = people.withIndex().associate { (i, uid) -> uid to i }

    /** SH-171: the day whose column header is under [position], or null. */
    fun headerAt(position: Offset): LocalDate? {
        if (position.x < sidebarPx || position.y >= headerPx || position.y < 0f || cellPx <= 0f) return null
        return dates.getOrNull(((position.x - sidebarPx + row.value) / cellPx).toInt())
    }

    /** SH-171: scrolls the least needed to show [date]'s column whole. */
    suspend fun revealDay(date: LocalDate) {
        val column = dates.indexOf(date)
        if (column < 0 || cellPx <= 0f) return
        val viewport = (widthPx - sidebarPx).coerceAtLeast(cellPx)
        val left = column * cellPx
        val right = left + cellPx
        when {
            left < row.value -> row.scrollTo(left.toInt())
            right > row.value + viewport -> row.scrollTo((right - viewport).toInt())
        }
    }

    /** The cell under [position], or null (the names, the day headers, past the last day). */
    fun cellAt(position: Offset): GridCell? {
        if (position.x < sidebarPx || position.y < headerPx || cellPx <= 0f) return null
        val column = ((position.x - sidebarPx + row.value) / cellPx).toInt()
        val date = dates.getOrNull(column) ?: return null
        val item = list.layoutInfo.visibleItemsInfo.firstOrNull { info ->
            info.key is String && info.key in personIndex && position.y >= info.offset && position.y < info.offset + info.size
        } ?: return null
        return GridCell(item.key as String, date)
    }

    /** The shift chip of [cell] under [position] (the chips share the cell's height), or null. */
    fun shiftAt(cell: GridCell, position: Offset): Shift? {
        val shifts = shiftsOf(cell)
        if (shifts.isEmpty()) return null
        val top = list.layoutInfo.visibleItemsInfo.firstOrNull { it.key == cell.userId }?.offset ?: return null
        val inner = (rowPx - 2 * cellPadPx).coerceAtLeast(1f)
        val index = (((position.y - top - cellPadPx) / inner) * shifts.size).toInt().coerceIn(0, shifts.lastIndex)
        return shifts[index]
    }

    /** Scrolls the least needed to show [cell] whole (under the day headers, beside the names). */
    suspend fun reveal(cell: GridCell) {
        val column = dates.indexOf(cell.date)
        if (column >= 0 && cellPx > 0f) {
            val viewport = (widthPx - sidebarPx).coerceAtLeast(cellPx)
            val left = column * cellPx
            val right = left + cellPx
            when {
                left < row.value -> row.scrollTo(left.toInt())
                right > row.value + viewport -> row.scrollTo((right - viewport).toInt())
            }
        }
        val index = personIndex[cell.userId] ?: return
        // Item 0 is the day headers.
        val itemIndex = index + 1
        var info = list.layoutInfo.visibleItemsInfo.firstOrNull { it.index == itemIndex }
        if (info == null) {
            list.scrollToItem(itemIndex)
            info = list.layoutInfo.visibleItemsInfo.firstOrNull { it.index == itemIndex } ?: return
        }
        val end = list.layoutInfo.viewportEndOffset
        when {
            info.offset < headerPx -> list.scrollBy(info.offset - headerPx)
            info.offset + info.size > end -> list.scrollBy((info.offset + info.size - end).toFloat())
        }
    }
}

/**
 * [from] moved by [columns] days and [rows] people, kept inside the grid; no cell yet: the first
 * person on [start] (today when the month shows it, else its first day).
 */
internal fun stepCell(people: List<String>, dates: List<LocalDate>, from: GridCell?, columns: Int, rows: Int, start: LocalDate? = null): GridCell? {
    if (people.isEmpty() || dates.isEmpty()) return null
    if (from == null || from.userId !in people || from.date !in dates) {
        return GridCell(people.first(), start?.takeIf { it in dates } ?: dates.first())
    }
    val person = (people.indexOf(from.userId) + rows).coerceIn(0, people.lastIndex)
    val day = (dates.indexOf(from.date) + columns).coerceIn(0, dates.lastIndex)
    return GridCell(people[person], dates[day])
}

/** The grid's interaction state: the keyboard's cell, the selection, the open popover, a drag. */
@Stable
class RosterGridState {
    /** The cell the keyboard is on (also the last cell clicked). */
    var cursor by mutableStateOf<GridCell?>(null)

    /** The keyboard was used last (the ring shows); a pointer press hides it again. */
    var keyboardActive by mutableStateOf(false)

    /** The grid has the focus. */
    var focused by mutableStateOf(false)
        internal set

    /** Empty cells picked with Shift / Ctrl / Cmd + click (or Space), for a template. */
    var selection by mutableStateOf<Set<GridCell>>(emptySet())

    /** SH-171: the day header the keyboard is on (a header clicked, or Up from the first person); null: a cell. */
    var headerCursor by mutableStateOf<LocalDate?>(null)

    /** SH-171: day headers picked with Shift / Ctrl / Cmd + click, Shift + the arrows or Space. */
    var headerSelection by mutableStateOf<Set<LocalDate>>(emptySet())

    /** SH-171: where a Shift range of day headers starts (the last header clicked or toggled). */
    var headerAnchor by mutableStateOf<LocalDate?>(null)

    /** The cell whose popover is open. */
    var popover by mutableStateOf<GridCell?>(null)

    /** The shift chip being dragged. */
    var drag by mutableStateOf<GridDrag?>(null)
        internal set

    internal val focusRequester = FocusRequester()
    internal var geometry: GridGeometry? = null

    /** The focus ring shows on [cursor]: the grid has the focus and the keyboard moves it. */
    val showsRing: Boolean get() = focused && keyboardActive

    fun toggle(cell: GridCell) {
        selection = if (cell in selection) selection - cell else selection + cell
    }

    fun clearSelection() {
        selection = emptySet()
    }

    fun toggleHeader(date: LocalDate) {
        headerSelection = if (date in headerSelection) headerSelection - date else headerSelection + date
        headerAnchor = date
    }

    /** SH-171: the days from the anchor (else the focused header) to [to], of [dates], selected. */
    fun selectHeaderRange(to: LocalDate, dates: List<LocalDate>) {
        val from = headerAnchor ?: headerCursor ?: to
        if (headerAnchor == null) headerAnchor = from
        val a = dates.indexOf(from)
        val b = dates.indexOf(to)
        if (a < 0 || b < 0) return
        headerSelection = dates.subList(minOf(a, b), maxOf(a, b) + 1).toSet()
    }

    /** SH-171: a plain click or arrow on a header: that day alone, the selection gone. */
    fun focusHeader(date: LocalDate) {
        headerCursor = date
        headerAnchor = date
        headerSelection = emptySet()
    }

    fun clearHeaderSelection() {
        headerSelection = emptySet()
    }

    /** SH-171: the days copied, or pasted from: the selected headers, else the header the keyboard is on. */
    val pickedDays: Set<LocalDate> get() = headerSelection.ifEmpty { setOfNotNull(headerCursor) }

    /** Opens [cell]'s popover. */
    fun open(cell: GridCell) {
        cursor = cell
        popover = cell
    }

    /** Closes the popover; the keyboard goes on in the grid. */
    fun closePopover() {
        popover = null
        runCatching { focusRequester.requestFocus() }
    }
}

/** What the grid's keys and pointer do, given by the screen (the view model behind it). */
internal class RosterGridActions(
    /** Whether the month can be changed here (else the keys only move and open). */
    val editable: Boolean,
    val onOpen: (GridCell) -> Unit,
    val onUnassign: (GridCell) -> Unit,
    val onDrop: (Shift, GridCell) -> Unit,
    val onZoom: (steps: Int) -> Unit,
    /** SH-171: Enter on a day header (its day pane or sheet). */
    val onOpenDay: (LocalDate) -> Unit = {},
    /** The day the keyboard starts on (today in this month, else its first day). */
    val startDate: LocalDate?
)

/**
 * The grid's keys, its pointer (drag and drop, selecting clicks, Ctrl + wheel, pinch) and its single
 * focus stop. Put on the box holding the grid; [state]'s geometry is set by the screen each layout.
 */
@Composable
internal fun Modifier.rosterGridInput(state: RosterGridState, actions: RosterGridActions): Modifier {
    val scope = rememberCoroutineScope()
    val current by rememberUpdatedState(actions)
    val label = stringResource(Res.string.roster_grid_label)
    return this
        .pointerInput(state) { zoomWithWheel { current.onZoom(it) } }
        .pointerInput(state) { zoomWithPinch { current.onZoom(it) } }
        .pointerInput(state) { gridPointer(state) { current } }
        .onKeyEvent { event -> gridKey(event, state, current, scope) }
        .semantics { contentDescription = label }
        .onFocusChanged { state.focused = it.isFocused }
        .focusRequester(state.focusRequester)
        .focusable()
        .testTag(RosterGridTags.GRID)
}

private fun gridKey(event: KeyEvent, state: RosterGridState, actions: RosterGridActions, scope: CoroutineScope): Boolean {
    if (event.type != KeyEventType.KeyDown) return false
    // Ctrl / Cmd / Alt + a key is a shortcut (the months, undo, copy, zoom): not the grid's.
    if (event.isCtrlPressed || event.isMetaPressed || event.isAltPressed) return false
    val geometry = state.geometry ?: return false
    fun move(columns: Int, rows: Int): Boolean {
        val next = stepCell(geometry.people, geometry.dates, state.cursor, columns, rows, actions.startDate) ?: return false
        state.headerCursor = null
        // Shift + an arrow adds the empty cells passed over to the selection.
        if (event.isShiftPressed && actions.editable) {
            listOfNotNull(state.cursor, next).filter { geometry.shiftsOf(it).isEmpty() }.forEach { state.selection += it }
        }
        state.cursor = next
        state.keyboardActive = true
        scope.launch { geometry.reveal(next) }
        return true
    }
    // SH-171: on a day header: Left / Right between days, Down back to the cells, Enter opens the
    // day, Space selects it, Esc clears the selected days.
    state.headerCursor?.let { day ->
        fun moveDay(columns: Int): Boolean {
            val index = (geometry.dates.indexOf(day) + columns).coerceIn(0, geometry.dates.lastIndex)
            val next = geometry.dates.getOrNull(index) ?: return false
            if (event.isShiftPressed && actions.editable) {
                if (state.headerAnchor == null || state.headerSelection.isEmpty()) state.headerAnchor = day
                state.selectHeaderRange(next, geometry.dates)
                state.headerCursor = next
            } else {
                state.focusHeader(next)
            }
            state.keyboardActive = true
            scope.launch { geometry.revealDay(next) }
            return true
        }
        return when (event.key) {
            Key.DirectionRight -> moveDay(1)
            Key.DirectionLeft -> moveDay(-1)
            Key.MoveHome -> moveDay(-geometry.dates.size)
            Key.MoveEnd -> moveDay(geometry.dates.size)
            Key.DirectionDown -> {
                val person = geometry.people.firstOrNull() ?: return true
                state.headerCursor = null
                state.cursor = GridCell(person, day)
                state.keyboardActive = true
                scope.launch { geometry.reveal(GridCell(person, day)) }
                true
            }
            Key.DirectionUp -> true
            Key.Enter, Key.NumPadEnter -> {
                state.keyboardActive = true
                actions.onOpenDay(day)
                true
            }
            Key.Spacebar -> {
                state.keyboardActive = true
                if (actions.editable) state.toggleHeader(day)
                true
            }
            Key.Escape -> {
                if (state.headerSelection.isNotEmpty()) {
                    state.clearHeaderSelection()
                    true
                } else false
            }
            else -> false
        }
    }
    val cursor = state.cursor
    if (event.key == Key.DirectionUp && cursor != null && cursor.userId == geometry.people.firstOrNull() && cursor.date in geometry.dates) {
        state.focusHeader(cursor.date)
        state.keyboardActive = true
        return true
    }
    return when (event.key) {
        Key.DirectionRight -> move(1, 0)
        Key.DirectionLeft -> move(-1, 0)
        Key.DirectionDown -> move(0, 1)
        Key.DirectionUp -> move(0, -1)
        Key.MoveHome -> move(-geometry.dates.size, 0)
        Key.MoveEnd -> move(geometry.dates.size, 0)
        Key.Enter, Key.NumPadEnter -> {
            val cell = cursor ?: return move(0, 0)
            state.keyboardActive = true
            actions.onOpen(cell)
            true
        }
        Key.Spacebar -> {
            val cell = cursor ?: return move(0, 0)
            state.keyboardActive = true
            if (actions.editable && geometry.shiftsOf(cell).isEmpty()) state.toggle(cell)
            true
        }
        Key.Delete, Key.Backspace -> {
            val cell = cursor ?: return false
            state.keyboardActive = true
            if (actions.editable) actions.onUnassign(cell)
            true
        }
        Key.Escape -> {
            if (state.selection.isNotEmpty() || state.headerSelection.isNotEmpty()) {
                state.clearSelection()
                state.clearHeaderSelection()
                true
            } else false
        }
        else -> false
    }
}

/** Ctrl (or Cmd) + the wheel: one step per notch (a trackpad's small steps add up first). */
private suspend fun androidx.compose.ui.input.pointer.PointerInputScope.zoomWithWheel(onZoom: (Int) -> Unit) {
    var pending = 0f
    awaitPointerEventScope {
        while (true) {
            val event = awaitPointerEvent(PointerEventPass.Initial)
            if (event.type != PointerEventType.Scroll) continue
            if (!(event.keyboardModifiers.isCtrlPressed || event.keyboardModifiers.isMetaPressed)) continue
            pending += event.changes.sumOf { it.scrollDelta.y.toDouble() }.toFloat()
            event.changes.forEach { it.consume() }
            if (abs(pending) >= 1f) {
                // The wheel away (up) zooms in, as in browsers.
                onZoom(if (pending < 0) 1 else -1)
                pending = 0f
            }
        }
    }
}

/**
 * The pointer on the grid: a press moves the keyboard's cell there (and puts the focus in the
 * grid); Shift / Ctrl / Cmd + a click on an empty cell selects it; a shift chip dragged (a mouse
 * or a stylus at once, a finger after a long press) drops onto the cell under it.
 */
/**
 * SH-161: two fingers pinch the cells' width, in the wheel's steps ([PinchZoomSteps]). Taken
 * before the grid's lists and cells see it, so a pinch neither scrolls nor starts a drag; one
 * finger is left to them.
 */
private suspend fun androidx.compose.ui.input.pointer.PointerInputScope.zoomWithPinch(onZoom: (Int) -> Unit) {
    awaitEachGesture {
        awaitFirstDown(requireUnconsumed = false, pass = PointerEventPass.Initial)
        val steps = PinchZoomSteps()
        do {
            val event = awaitPointerEvent(PointerEventPass.Initial)
            if (event.changes.count { it.pressed && it.type != PointerType.Mouse } >= 2) {
                val n = steps.add(event.calculateZoom())
                if (n != 0) onZoom(n)
                event.changes.forEach { it.consume() }
            }
        } while (event.changes.any { it.pressed })
    }
}

private suspend fun androidx.compose.ui.input.pointer.PointerInputScope.gridPointer(state: RosterGridState, actions: () -> RosterGridActions) {
    awaitEachGesture {
        val down = awaitFirstDown(requireUnconsumed = false, pass = PointerEventPass.Initial)
        val event = currentEvent
        val geometry = state.geometry ?: return@awaitEachGesture
        // SH-171: a day header: the keyboard goes there; Shift + click selects a range, Ctrl / Cmd +
        // click one day more (a plain click goes on to the header, which opens the day; a right
        // click is its menu).
        geometry.headerAt(down.position)?.let { day ->
            if (down.type == PointerType.Mouse && !event.buttons.isPrimaryPressed) return@awaitEachGesture
            state.keyboardActive = false
            runCatching { state.focusRequester.requestFocus() }
            val modifiers = event.keyboardModifiers
            when {
                actions().editable && modifiers.isShiftPressed -> {
                    // A range from the last header clicked.
                    down.consume()
                    state.selectHeaderRange(day, geometry.dates)
                    state.headerCursor = day
                }
                actions().editable && (modifiers.isCtrlPressed || modifiers.isMetaPressed) -> {
                    // One day more (or less).
                    down.consume()
                    if (state.headerSelection.isEmpty()) state.headerCursor?.takeIf { it != day }?.let { state.headerSelection = setOf(it) }
                    state.toggleHeader(day)
                    state.headerCursor = day
                }
                else -> state.focusHeader(day)
            }
            return@awaitEachGesture
        }
        val cell = geometry.cellAt(down.position) ?: return@awaitEachGesture
        // A right click is the cell's menu (ContextMenuArea).
        if (down.type == PointerType.Mouse && !event.buttons.isPrimaryPressed) return@awaitEachGesture
        state.keyboardActive = false
        runCatching { state.focusRequester.requestFocus() }
        state.headerCursor = null
        val editable = actions().editable
        val modifiers = event.keyboardModifiers
        if (editable && (modifiers.isShiftPressed || modifiers.isCtrlPressed || modifiers.isMetaPressed)) {
            // A selecting click: used here (the cell's popover and the Control-click menu leave it);
            // only an empty cell joins the selection.
            down.consume()
            state.cursor = cell
            if (geometry.shiftsOf(cell).isEmpty()) state.toggle(cell)
            return@awaitEachGesture
        }
        state.cursor = cell
        if (!editable) return@awaitEachGesture
        val shift = geometry.shiftAt(cell, down.position)?.takeIf { !it.id.isPending } ?: return@awaitEachGesture
        val start: Offset = if (down.type == PointerType.Touch) {
            // A finger scrolls the grid; it drags only after holding still.
            awaitLongPressOrCancellation(down.id)?.position ?: return@awaitEachGesture
        } else {
            var reached: Offset? = null
            while (reached == null) {
                val move = awaitPointerEvent(PointerEventPass.Initial)
                val change = move.changes.firstOrNull { it.id == down.id } ?: return@awaitEachGesture
                if (!change.pressed) return@awaitEachGesture
                if ((change.position - down.position).getDistance() > viewConfiguration.touchSlop) {
                    change.consume()
                    reached = change.position
                }
            }
            reached
        }
        state.drag = GridDrag(shift, cell, start, geometry.cellAt(start))
        try {
            while (true) {
                val move = awaitPointerEvent(PointerEventPass.Initial)
                val change = move.changes.firstOrNull { it.id == down.id } ?: break
                change.consume()
                val target = geometry.cellAt(change.position)
                if (!change.pressed) {
                    if (target != null && target != cell) actions().onDrop(shift, target)
                    break
                }
                state.drag = state.drag?.copy(pointer = change.position, target = target)
            }
        } finally {
            state.drag = null
        }
    }
}

/** The ring of the keyboard's cell, the selection's tint or a drop target's outline over a cell. */
@Composable
internal fun BoxScope.GridCellMarks(focusRing: Boolean, selected: Boolean, dropTarget: Boolean) {
    if (selected) {
        Box(
            Modifier.matchParentSize().padding(3.dp).clip(RoundedCornerShape(10.dp))
                .background(DarkGold.copy(alpha = 0.28f))
                .border(1.5.dp, DarkGold, RoundedCornerShape(10.dp))
        )
    }
    if (dropTarget) {
        Box(
            Modifier.matchParentSize().padding(2.dp)
                .border(2.dp, DeepNavy, RoundedCornerShape(10.dp))
                .background(DeepNavy.copy(alpha = 0.08f), RoundedCornerShape(10.dp))
                .testTag(RosterGridTags.DROP_TARGET)
        )
    }
    if (focusRing) {
        Box(
            Modifier.matchParentSize().padding(1.dp)
                .border(2.5.dp, DeepNavy, RoundedCornerShape(11.dp))
                .padding(2.5.dp)
                .border(1.dp, Color.White, RoundedCornerShape(9.dp))
                .testTag(RosterGridTags.FOCUS_RING)
        )
    }
}

/** SH-171: a day header's marks: selected (gold), copied (a lighter gold), the keyboard's ring. */
@Composable
internal fun BoxScope.DayHeaderMarks(focusRing: Boolean, selected: Boolean, copied: Boolean) {
    if (selected || copied) {
        Box(
            Modifier.matchParentSize().padding(3.dp).clip(RoundedCornerShape(10.dp))
                .background(DarkGold.copy(alpha = if (selected) 0.28f else 0.12f))
                .border(1.5.dp, DarkGold.copy(alpha = if (selected) 1f else 0.7f), RoundedCornerShape(10.dp))
        )
    }
    if (focusRing) {
        Box(
            Modifier.matchParentSize().padding(1.dp)
                .border(2.5.dp, DeepNavy, RoundedCornerShape(11.dp))
                .padding(2.5.dp)
                .border(1.dp, Color.White, RoundedCornerShape(9.dp))
                .testTag(RosterGridTags.HEADER_RING)
        )
    }
}

/** The dragged chip under the pointer: its times, half see-through. */
@Composable
internal fun BoxScope.DragGhost(drag: GridDrag, widthPx: Float) {
    val density = androidx.compose.ui.platform.LocalDensity.current
    val width = with(density) { widthPx.toDp() }.coerceIn(64.dp, 140.dp)
    val height = 44.dp
    val halfW = with(density) { (width / 2).toPx() }
    val halfH = with(density) { (height / 2).toPx() }
    Box(
        Modifier
            .align(Alignment.TopStart)
            .offset { IntOffset((drag.pointer.x - halfW).toInt(), (drag.pointer.y - halfH).toInt()) }
            .size(width, height)
            .shadow(8.dp, RoundedCornerShape(10.dp))
            .clip(RoundedCornerShape(10.dp))
            .background(DarkMetallicBackgroundBrush, alpha = 0.88f)
            .border(1.dp, DarkGold, RoundedCornerShape(10.dp))
            .testTag(RosterGridTags.DRAG_GHOST),
        contentAlignment = Alignment.Center
    ) {
        Text(drag.shift.plannedRange(), color = Color.White, style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
    }
}

/**
 * Where a cell's popover goes: right of the cell when it fits, else left of it, else against the
 * window's right edge; its top level with the cell's, moved up as far as the window needs.
 */
internal fun cellPopoverOffset(anchor: IntRect, window: IntSize, popup: IntSize, gap: Int): IntOffset {
    val right = anchor.right + gap
    val left = anchor.left - gap - popup.width
    val x = when {
        right + popup.width <= window.width -> right
        left >= 0 -> left
        else -> (window.width - popup.width).coerceAtLeast(0)
    }
    val y = anchor.top.coerceAtMost(window.height - popup.height).coerceAtLeast(0)
    return IntOffset(x, y)
}

private class CellPopoverPosition(private val gap: Int) : PopupPositionProvider {
    override fun calculatePosition(anchorBounds: IntRect, windowSize: IntSize, layoutDirection: LayoutDirection, popupContentSize: IntSize): IntOffset =
        cellPopoverOffset(anchorBounds, windowSize, popupContentSize, gap)
}

/**
 * A cell's popover beside it (wide windows; phones have no grid): the navy of the app's sheets,
 * [title] and [subtitle], a close button; a click outside or Esc closes it.
 */
@Composable
internal fun CellPopover(title: String, subtitle: String, onDismiss: () -> Unit, content: @Composable () -> Unit) {
    val gap = with(androidx.compose.ui.platform.LocalDensity.current) { 6.dp.roundToPx() }
    val position = remember(gap) { CellPopoverPosition(gap) }
    Popup(popupPositionProvider = position, onDismissRequest = onDismiss, properties = PopupProperties(focusable = true)) {
        val cardFocus = remember { androidx.compose.ui.focus.FocusRequester() }
        com.evoapps.evoshift.widgets.ProvideDialogKeyboardDismiss(cardFocus) {
            ProvideDialogColors { CellPopoverCard(title, subtitle, onDismiss, cardFocus, content) }
        }
    }
}

@Composable
private fun CellPopoverCard(
    title: String,
    subtitle: String,
    onDismiss: () -> Unit,
    focus: androidx.compose.ui.focus.FocusRequester,
    content: @Composable () -> Unit
) {
    Column(
        Modifier
            .widthIn(max = 400.dp)
            .width(380.dp)
            .heightIn(max = 600.dp)
            .padding(4.dp)
            .shadow(16.dp, RoundedCornerShape(20.dp))
            .clip(RoundedCornerShape(20.dp))
            .background(DarkMetallicBackgroundBrush)
            .border(1.dp, DarkGold.copy(alpha = 0.5f), RoundedCornerShape(20.dp))
            .dialogKeys(onEscape = onDismiss, onEnter = null, focus = focus)
            .testTag(RosterGridTags.POPOVER)
    ) {
        Row(Modifier.fillMaxWidth().padding(start = 18.dp, end = 6.dp, top = 10.dp), verticalAlignment = Alignment.CenterVertically) {
            Icon(Icons.Default.Person, contentDescription = null, tint = LightGold, modifier = Modifier.size(20.dp))
            Spacer(Modifier.width(10.dp))
            Column(Modifier.weight(1f)) {
                Text(title, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = LightGold)
                Text(subtitle, style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha = 0.75f))
            }
            IconButton(onClick = onDismiss, modifier = Modifier.handCursor()) {
                Icon(Icons.Default.Close, contentDescription = stringResource(Res.string.close), tint = Color.White)
            }
        }
        Column(Modifier.verticalScroll(rememberScrollState()).padding(start = 18.dp, end = 18.dp, top = 8.dp, bottom = 18.dp)) {
            content()
        }
    }
}

/**
 * "Zaznaczono: 5 · [Przydziel szablon ▾] [Wyczyść]" over the grid while cells are selected: a
 * template from the workplace's shift templates goes on every selected cell (one Undo).
 */
@Composable
internal fun BoxScope.SelectionBar(count: Int, templates: List<ShiftTemplate>, onApply: (ShiftTemplate) -> Unit, onClear: () -> Unit) {
    var menu by remember { mutableStateOf(false) }
    Row(
        Modifier
            .align(Alignment.BottomCenter)
            // Above the snackbar's place (an Undo shows there while cells are picked again).
            .padding(bottom = 88.dp)
            .shadow(10.dp, RoundedCornerShape(28.dp))
            .clip(RoundedCornerShape(28.dp))
            .background(DarkMetallicBackgroundBrush)
            .border(1.dp, DarkGold.copy(alpha = 0.6f), RoundedCornerShape(28.dp))
            .padding(start = 20.dp, end = 8.dp, top = 4.dp, bottom = 4.dp)
            .testTag(RosterGridTags.SELECTION_BAR),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Text(stringResource(Res.string.roster_selected_count, count), color = Color.White, fontWeight = FontWeight.Bold)
        Text("·", color = LightGold)
        Box {
            TextButton(onClick = { menu = true }, modifier = Modifier.handCursor().testTag(RosterGridTags.SELECTION_APPLY)) {
                Text(stringResource(Res.string.roster_selection_apply), color = LightGold, fontWeight = FontWeight.Bold)
                Icon(Icons.Default.ArrowDropDown, contentDescription = null, tint = LightGold)
            }
            DropdownMenu(expanded = menu, onDismissRequest = { menu = false }) {
                if (templates.isEmpty()) {
                    DropdownMenuItem(text = { Text(stringResource(Res.string.roster_selection_no_templates)) }, onClick = { menu = false }, enabled = false)
                }
                templates.forEach { template ->
                    DropdownMenuItem(
                        text = { Text(listOfNotNull(template.name.takeIf { it.isNotBlank() }, "${template.startTime.take(5)}–${template.endTime.take(5)}").joinToString(" · ")) },
                        onClick = {
                            menu = false
                            onApply(template)
                        },
                        modifier = Modifier.handCursor().testTag(RosterGridTags.template(template.id))
                    )
                }
            }
        }
        TextButton(onClick = onClear, modifier = Modifier.handCursor().testTag(RosterGridTags.SELECTION_CLEAR)) {
            Text(stringResource(Res.string.roster_selection_clear), color = Color.White)
        }
    }
}
